import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle, ArrowLeft, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/seo';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const PLANS = {
  starter: {
    name: 'Starter',
    price: 29,
    videoLimit: 30,
  },
  pro: {
    name: 'Pro',
    price: 69,
    videoLimit: 100,
  },
};

const SubscriptionCancel: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'starter';
  const plan = PLANS[planId as keyof typeof PLANS] || PLANS.starter;

  return (
    <div className="min-h-screen flex items-center justify-center p-6" 
      style={{ background: `linear-gradient(135deg, ${PALETTE.brightSnow} 0%, #f5f5f5 100%)` }}>
      <SEO
        title="Subscription Cancelled"
        description="Your BrandComply subscription checkout was cancelled. You can try again anytime."
        path="/subscription/cancel"
      />
      <Card className="w-full max-w-md border-0 shadow-xl">
        <CardContent className="p-8 text-center">
          {/* Cancel Icon */}
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full"
              style={{ background: `${PALETTE.americanSilver}20` }}>
              <XCircle className="w-10 h-10" style={{ color: PALETTE.americanSilver }} />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
            Payment Cancelled
          </h1>
          <p className="text-base mb-6" style={{ color: `${PALETTE.gunmetal}70` }}>
            No worries! Your payment was not processed. You can try again when you're ready.
          </p>

          {/* Why Subscribe Info */}
          <div className="text-left p-4 rounded-lg mb-6" style={{ background: `${PALETTE.americanSilver}10` }}>
            <h3 className="font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
              With {plan.name} Plan You Get:
            </h3>
            <ul className="space-y-1 text-sm" style={{ color: `${PALETTE.gunmetal}80` }}>
              <li>• <strong>{plan.videoLimit}</strong> video reviews per month</li>
              <li>• Unlimited campaigns</li>
              <li>• Unlimited shareable submission links</li>
              <li>• Just <strong>€{plan.price}/month</strong></li>
              <li>• Cancel anytime</li>
            </ul>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/subscription')}
              className="w-full h-12 rounded-full text-base font-semibold"
              style={{ 
                background: PALETTE.inchworm, 
                color: PALETTE.gunmetal 
              }}
            >
              Subscribe Now
            </Button>
            
            <Button
              onClick={() => navigate('/brand/campaign')}
              variant="outline"
              className="w-full h-12 rounded-full text-base font-semibold"
              style={{ 
                background: PALETTE.brightSnow,
                color: PALETTE.gunmetal,
                border: `1px solid ${PALETTE.americanSilver}`
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>

          {/* Contact Support */}
          <div className="mt-6 pt-6 border-t" style={{ borderColor: `${PALETTE.americanSilver}40` }}>
            <p className="text-sm mb-2" style={{ color: `${PALETTE.gunmetal}60` }}>
              Have questions about pricing?
            </p>
            <a 
              href="mailto:info@brandcomply.ai"
              className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity"
              style={{ color: PALETTE.gunmetal }}
            >
              <MessageCircle className="w-4 h-4" />
              Contact Support
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionCancel;

